/* eslint-disable react-refresh/only-export-components */
/**
 * Keyboard Navigation Context Provider
 * Handles keyboard user detection and navigation state
 */

import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';

interface KeyboardNavigation {
  isKeyboardUser: boolean;
  enableKeyboardMode: () => void;
  disableKeyboardMode: () => void;
}

const KeyboardNavigationContext = createContext<KeyboardNavigation | undefined>(undefined);

export const useKeyboardNavigation = () => {
  const context = useContext(KeyboardNavigationContext);
  if (context === undefined) {
    throw new Error('useKeyboardNavigation must be used within a KeyboardNavigationProvider');
  }
  return context;
};

interface KeyboardNavigationProviderProps {
  children: React.ReactNode;
}

export const KeyboardNavigationProvider: React.FC<KeyboardNavigationProviderProps> = ({ children }) => {
  const [isKeyboardUser, setIsKeyboardUser] = useState(false);

  // Detect keyboard usage
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        setIsKeyboardUser(true);
      }
    };

    const handleMouseDown = () => {
      setIsKeyboardUser(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  const enableKeyboardMode = useCallback(() => {
    setIsKeyboardUser(true);
  }, []);

  const disableKeyboardMode = useCallback(() => {
    setIsKeyboardUser(false);
  }, []);

  const contextValue: KeyboardNavigation = {
    isKeyboardUser,
    enableKeyboardMode,
    disableKeyboardMode,
  };

  return (
    <KeyboardNavigationContext.Provider value={contextValue}>
      {children}
    </KeyboardNavigationContext.Provider>
  );
};